<template>
  <div class="image-slider">
    <!-- Thumbnails -->
    <div class="image-slider__thumbnail-wrap">
      <flickity
        ref="flickityThumbnail"
        :options="optionsThumbnail"
      >
        <div
          v-for="(item, index) in items"
          class="image-slider__thumbnail-cell"
        >
          <button
            @click="enterFullscreen(index)"
            class="image-slider__thumbnail-button"
          >
          <span
            class="image-slider__thumbnail-image"
            :style="`background-image: url(${itemThumbnailUrl(item)})`"
          />
          <span
            v-if="item.type === 'video'"
            class="image-slider__thumbnail-icon"
          >
            <PlayIcon />
          </span>
          </button>
        </div>
      </flickity>

      <button
        class="image-slider__nav-button image-slider__nav-button--left"
        :class="`image-slider__nav-button--${color}`"
        @click="thumbnailPrevious"
        v-if="!thumbnailAtStart"
      >
        <ArrowIcon />
      </button>
      <button
        class="image-slider__nav-button image-slider__nav-button--right"
        :class="`image-slider__nav-button--${color}`"
        @click="thumbnailNext"
        v-if="!thumbnailAtEnd"
      >
        <ArrowIcon />
      </button>
    </div>

    <!-- Fullscreen -->
    <Portal>
      <Flickity
        ref="flickityFullscreen"
        class="image-slider__fullscreen"
        :options="optionsFullscreen"
        v-show="fullscreen"
      >
        <div
          v-for="item in items"
          class="image-slider__fullscreen-cell"
        >
          <img
            v-if="item.type === 'image'"
            class="image-slider__fullscreen-image"
            :data-flickity-lazyload-src="item.large"
          >
          <div
            v-else-if="item.type === 'video'"
            class="image-slider__fullscreen-video print-hide"
          >
            <div class="image-slider__fullscreen-video-wrapper">
              <iframe
                v-if="fullscreen"
                :src="`https://www.youtube.com/embed/${item.id}/`"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                class="image-slider__fullscreen-video-iframe"
              ></iframe>
            </div>
          </div>
        </div>
      </Flickity>
    </Portal>
  </div>
</template>

<script>
  import {Portal} from '@linusborg/vue-simple-portal';
  import Flickity from 'vue-flickity';
  import ArrowIcon from './icons/ArrowIcon.vue';
  import PlayIcon from './icons/PlayIcon.vue';

  export default {
    components: {
    Flickity,
    Portal,
    ArrowIcon,
    PlayIcon
},
    props: {
      items: {
        type: Array,
        required: true,
      },
      color: {
        type: String,
        default: 'default',
      },
    },
    data: () => ({
      fullscreen: false,
      thumbnailAtEnd: false,
      thumbnailAtStart: false,
      optionsThumbnail: {
        pageDots: false,
        prevNextButtons: false,
        contain: true,
        groupCells: true,
      },
      optionsFullscreen: {
        fullscreen: true,
        lazyLoad: true,
      },
    }),
    methods: {
      thumbnailPrevious() {
        this.$refs.flickityThumbnail.previous();
      },
      thumbnailNext() {
        this.$refs.flickityThumbnail.next();
      },
      onThumbnailSelect(index) {
        const slidesLength = this.$refs.flickityThumbnail.$flickity.slides.length;
        this.thumbnailAtStart = index === 0;
        this.thumbnailAtEnd = index === slidesLength - 1;
      },
      onFullscreenChange(isFullscreen) {
        this.fullscreen = isFullscreen;
      },
      async enterFullscreen(index) {
        const flickityFullscreen = this.$refs.flickityFullscreen.$flickity;
        flickityFullscreen.select(index, false, true);
        flickityFullscreen.viewFullscreen();

        // Not sure what exactly are we waiting to happen for *two* ticks but unless we do, the slider size won't be
        // initialized. Probably has something to do with portal since it seems like it break when we switched to that.
        await this.$nextTick();
        await this.$nextTick();
        flickityFullscreen.resize();
      },
      itemThumbnailUrl(item) {
        return item.type === 'image'
          ? item.small
          : `https://img.youtube.com/vi/${item.id}/hqdefault.jpg\n`;
      },
    },
    async mounted() {
      // https://github.com/LinusBorg/vue-simple-portal/issues/14
      await this.$nextTick();

      this.$refs.flickityFullscreen.$flickity.on('fullscreenChange', this.onFullscreenChange);

      const flickityThumbnail = this.$refs.flickityThumbnail.$flickity;
      flickityThumbnail.on('select', this.onThumbnailSelect);
      flickityThumbnail.select(0);
    },
    destroyed() {
      this.$refs.flickityFullscreen.$flickity.off('fullscreenChange', this.onFullscreenChange);
      this.$refs.flickityThumbnail.$flickity.off('select', this.onThumbnailSelect);
    },
  }
</script>

<style lang="scss" scoped>
  @import "~styles/definitions";

  .image-slider {
  }

  .image-slider__thumbnail-wrap {
    position: relative;
    margin: r(-24) 0;
  }

  .image-slider__thumbnail-cell {
    margin: r(24) r(8);

    &:first-child {
      padding-left: r(48);
    }

    &:last-child {
      padding-right: r(48);
    }
  }

  .image-slider__thumbnail-button {
    @include reset-button(true);
    position: relative;
    transition: box-shadow 0.2s;

    &:focus {
      box-shadow: 0 0 0 r(3) rgba($color-white, 0.5);
    }
  }

  .image-slider__thumbnail-image {
    @include bg-cover;
    display: block;
    width: r(216);
    height: r(144);
    box-shadow: 0 0 r(19) 0 rgba(0, 0, 0, 0.5);
    border: r(4) solid $color-white;
  }

  .image-slider__thumbnail-icon {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    color: $color-white;
  }

  .image-slider__nav-button {
    @include reset-button(true);
    @include square(r(56));
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: r(28);
    color: $color-white;
    background-color: $color-green-2025;
    margin: 0 r(12);
    transition: box-shadow 0.2s;

    &:focus {
      box-shadow: 0 0 0 r(4) rgba($color-blue-dirty, 0.5);
    }

    &--yellow {
      background-color: $color-yellow;

      &:focus {
        box-shadow: 0 0 0 r(4) rgba($color-yellow, 0.5);
      }
    }

    &--pink {
      background-color: $color-pink;

      &:focus {
        box-shadow: 0 0 0 r(4) rgba($color-pink, 0.5);
      }
    }

    &--left {
      left: 0;
      transform: translateY(-50%) rotateZ(180deg);
    }

    &--right {
      right: 0;
    }
  }

  .image-slider__fullscreen {
    z-index: 200 !important;
    padding-bottom: r(64) !important;

    .flickity-page-dots {
      bottom: r(22) !important;
    }

    .flickity-prev-next-button {
      z-index: 1;

    }

    @media (min-width: 62rem) {
      .flickity-prev-next-button {
        width: 6em;
        height: 6em;
        border: .4em solid #0004;
      }
      .flickity-fullscreen-button {
        width: 5em;
        height: 5em;
        border: .4em solid #0004;
        right: 2em;
        top: 2em;
      }
      .flickity-fullscreen-button .flickity-button-icon {
        width: 70%;
        height: 70%;
        top: 15%;
        left: 15%;
      }
      .flickity-prev-next-button.previous {
        left: 2em;
      }
      .flickity-prev-next-button.next {
        right: 2em;
      }
    }
  }

  .image-slider__fullscreen-cell {
    @include bg-cover;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .image-slider__fullscreen-image {
    max-height: 100%;
    max-width: 100%;
    height: auto;
    width: auto;
    display: block;
  }

  .image-slider__fullscreen-video {
    flex: 1 0 100%;
    position: relative;
    width: 100%;
    height: 100%;
  }

  .image-slider__fullscreen-video-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .image-slider__fullscreen-video-iframe {
    // position: absolute;
    // top: 0;
    // left: 0;
    // right: 0;
    // bottom: 0;
    // width: 100%;
    // height: 100%;
    border: none;
    aspect-ratio: 16 / 9;
  }

  @media (min-aspect-ratio: 16/9) {
  .image-slider__fullscreen-video-iframe {
    height: 100%;
    max-height: 720px;
  }
}
@media (max-aspect-ratio: 16/9) {
  .image-slider__fullscreen-video-iframe {
    width: 100%;
    max-width: 1280px;
  }
}


</style>
